<template>
  <div
    class="references-wrapper"
    :class="{
      'drop-indicator-before': dropIndicator === 'before',
      'drop-indicator-after': dropIndicator === 'after',
    }"
    :data-attr="!isEditable ? 'citation-inserted' : undefined"
    @click="$emit('select')"
  >
    <div
      class="list-item draggable-wrapper"
      :class="{
        'is-selected': isEditable && isSelected,
      }"
      :data-index="index"
      :draggable="isEditable"
      @dragend="handleDragEnd"
      @dragstart="handleDragStart"
    >
      <BbIcon v-if="isEditable" size="small" name="burger" class="drag-handle" />

      <div class="reference-content">
        <ReferenceDetailsLabels v-if="isSecondOrderReferencesOrg" :source="source" :doc-type="docType" />

        <div class="reference-content-row">
          <CustomReferenceListItem
            v-if="reference.addedByUser"
            :title="customTitle"
            :is-editable-title="isEditableTitle"
            :display-icon="isEditable"
            @cancel="cancelEdit"
            @set-editable="isEditableTitle = true"
            @update="(value) => updateCustomTitle(reference.id, value)"
            @delete="confirmDeletePopupOpened = true"
          />

          <template v-else>
            <div class="thumbnail-container">
              <img v-if="getThumbUrl(reference)" :src="getThumbUrl(reference)" alt="thumb" class="thumbnail-image" />
              <div v-else class="default-thumb" />
            </div>
            <div class="footnote-details">
              <div v-if="reference.customFootnote" class="title-with-custom">
                <div class="title-with-custom__ai">
                  <TextBody i18n-key="author.aiOriginal" />
                  <br />
                  {{ reference.footnoteString }}
                </div>
                <div class="title-with-custom__custom">
                  <TextBody i18n-key="common.current" />
                  <br />
                  <EditableReferenceField
                    :value="customTitle"
                    :isEditing="isEditableTitle"
                    :editable="isEditable"
                    :resettable="true"
                    :ellipsis="false"
                    @cancel="cancelEdit"
                    @set-editable="isEditableTitle = true"
                    @update="(value) => updateCustomTitle(reference.id, value)"
                    @delete="confirmDeletePopupOpened = true"
                    @reset="confirmResetPopupOpened = true"
                  />
                </div>
              </div>
              <div v-else>
                <EditableReferenceField
                  :value="title"
                  :isEditing="isEditableTitle"
                  :editable="isEditable"
                  :ellipsis="false"
                  @cancel="cancelEdit"
                  @set-editable="isEditableTitle = true"
                  @update="(value) => updateTitle(reference.id, value)"
                  @delete="confirmDeletePopupOpened = true"
                />
              </div>
            </div>
          </template>
        </div>
      </div>

      <WarningPopup
        :popup-opened="confirmDeletePopupOpened"
        title-key="common.delete"
        warning-message-key="author.warningDeleteReference"
        confirm-text-key="modals.confirmDeleteText"
        @update:open="confirmDeletePopupOpened = false"
        @execute:action="removeReference"
      />

      <WarningPopup
        :popup-opened="confirmResetPopupOpened"
        title-key="common.reset"
        warning-message-key="author.warningResetReference"
        confirm-text-key="modals.confirmResetText"
        @update:open="confirmResetPopupOpened = false"
        @execute:action="resetReference"
      />
    </div>

    <!-- Nested sources cited within this document — no move/drag (not their own footnote), just the
    same thumbnail with the source's own text instead of the document's title, plus (in manage mode)
    a delete action that removes this one source everywhere it's cited (see onRemoveSecondOrder). -->
    <div
      v-for="(entry, soIndex) in reference.secondOrderReferences"
      :key="`${index}-so-${soIndex}`"
      class="list-item second-order-list-item"
      :class="{ 'is-selected': isEditable && selectedSecondOrderIndex === soIndex }"
      :data-attr="!isEditable ? 'citation-inserted' : undefined"
      @click.stop="onSelectSecondOrder(soIndex, entry)"
    >
      <!-- Reserves the exact same width as the main row's drag-handle icon above (not draggable
      itself, never shown — just keeps the thumbnail/text aligned under the main row's). -->
      <BbIcon v-if="isEditable" size="small" name="burger" class="drag-handle drag-handle--spacer" />

      <div class="reference-content">
        <ReferenceDetailsLabels v-if="isSecondOrderReferencesOrg" :source="secondOrderSource" :doc-type="docType" />
        <!-- Thumbnail is the parent document's, not this source's — caption names which document cited it. -->
        <div class="second-order-parent-name">
          <TextBody tag="span" i18n-key="author.citedFromLabel" weight="medium" />
          <TextBody tag="span" :text="reference.footnoteString" weight="medium" className="second-order-parent-name__value" />
          <BbTooltip
            v-if="isEditable"
            v-bind="{ placement: 'top', offset: 4, isDisabled: false, content: t('common.delete') }"
          >
            <BbBtn
              class="second-order-delete-icon-btn"
              type="icon"
              size="small"
              icon="delete"
              icon-size="small"
              icon-type="outlined"
              height="36"
              width="40"
              no-background
              @click.stop="confirmRemoveSecondOrderIndex = soIndex"
            />
          </BbTooltip>
        </div>
        <div class="reference-content-row">
          <div class="second-order-left">
            <div class="thumbnail-container">
              <img v-if="getThumbUrl(reference)" :src="getThumbUrl(reference)" alt="thumb" class="thumbnail-image" />
              <div v-else class="default-thumb" />
            </div>
          </div>
          <div class="footnote-details">
            <!-- A source cited by multiple parents shows once, here, with the rest rolled into this note. -->
            <TextBody
              v-if="entry.moreDocumentsCount > 0"
              tag="span"
              i18n-key="author.andMoreDocuments"
              :i18n-params="{ count: entry.moreDocumentsCount }"
              className="second-order-more-documents"
            />
            <div v-if="entry.customFootnote" class="title-with-custom">
              <div class="title-with-custom__ai">
                <TextBody i18n-key="author.aiOriginal" />
                <br />
                {{ entry.originalText }}
              </div>
              <div class="title-with-custom__custom">
                <TextBody i18n-key="common.current" />
                <br />
                <EditableReferenceField
                  :value="entry.customFootnote"
                  :isEditing="editableSecondOrderIndex === soIndex"
                  :editable="isEditable"
                  :resettable="true"
                  :ellipsis="false"
                  @cancel="cancelSecondOrderEdit"
                  @set-editable="editableSecondOrderIndex = soIndex"
                  @update="(value) => updateSecondOrderText(entry, value)"
                  @reset="confirmResetSecondOrderIndex = soIndex"
                />
              </div>
            </div>
            <div v-else>
              <!-- Nothing customized yet, so nothing to reset — edit only. -->
              <EditableReferenceField
                :value="entry.text"
                :isEditing="editableSecondOrderIndex === soIndex"
                :editable="isEditable"
                :show-secondary-action="false"
                :ellipsis="false"
                @cancel="cancelSecondOrderEdit"
                @set-editable="editableSecondOrderIndex = soIndex"
                @update="(value) => updateSecondOrderText(entry, value)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <WarningPopup
      :popup-opened="confirmResetSecondOrderIndex !== -1"
      title-key="common.reset"
      warning-message-key="author.warningResetReference"
      confirm-text-key="modals.confirmResetText"
      @update:open="confirmResetSecondOrderIndex = -1"
      @execute:action="resetSecondOrderReference"
    />

    <WarningPopup
      :popup-opened="confirmRemoveSecondOrderIndex !== -1"
      title-key="common.delete"
      warning-message-key="author.warningDeleteReference"
      confirm-text-key="modals.confirmDeleteText"
      @update:open="confirmRemoveSecondOrderIndex = -1"
      @execute:action="removeSecondOrderReference"
    />
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import TextBody from '~/components/common/TextBody.vue';
import WarningPopup from '~/components/common/WarningPopup.vue';
import CustomReferenceListItem from '~/components/conversation/authoring/references/CustomReferenceListItem.vue';
import EditableReferenceField from '~/components/conversation/authoring/references/EditableReferenceField.vue';
import ReferenceDetailsLabels from '~/components/conversation/referencePanel/ReferenceDetailsLabels.vue';
import { resolveCitationSourceInfo } from '~/utils/citation-source';
import useSecondOrderReferencesGating from '~/composables/useSecondOrderReferencesGating';

export default {
  components: {
    CustomReferenceListItem,
    WarningPopup,
    EditableReferenceField,
    TextBody,
    ReferenceDetailsLabels,
  },
  emits: [
    'select',
    'remove',
    'update',
    'reset',
    'select-second-order',
    'update-second-order',
    'reset-second-order',
    'remove-second-order',
  ],
  props: {
    index: {
      type: Number,
      required: true,
    },
    reference: {
      type: Object,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isDragging: {
      type: Boolean,
      default: false,
    },
    dropIndicator: {
      type: String,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const { isSecondOrderReferencesOrg } = useSecondOrderReferencesGating();

    const docType = computed(() => (props.reference?.addedByUser ? '' : props.reference?.citation_metadata?.doc_type || ''));
    const source = computed(() => {
      if (props.reference?.addedByUser) {
        return { name: '', logo: null, type: 'Manually added', unsubstantiated: true, isPrimarySource: false, docType: '' };
      }

      // Decided purely by isPrimarySource, independent of any nested second-order sources it
      // contains — those get their own always-substantiated sub-rows below.
      const isPrimarySource = Boolean(props.reference?.citation_metadata?.is_primary_source);

      return resolveCitationSourceInfo({
        externalId: props.reference?.externalId || props.reference?.id,
        unsubstantiated: !isPrimarySource,
        isPrimarySource,
        docType: docType.value,
      });
    });

    const secondOrderSource = computed(() =>
      resolveCitationSourceInfo({
        externalId: props.reference?.externalId || props.reference?.id,
        unsubstantiated: false,
        isPrimarySource: false,
        docType: docType.value,
      }),
    );

    const title = computed({
      get: () => props.reference.footnoteString,
      set: (value) => {
        emit('update', value);
        isEditableTitle.value = false;
      },
    });
    const customTitle = computed({
      get: () => props.reference.customFootnote,
      set: (value) => {
        emit('update', value);
        isEditableTitle.value = false;
      },
    });
    const isEditableTitle = ref(false);
    const confirmDeletePopupOpened = ref(false);
    const confirmResetPopupOpened = ref(false);

    const cancelEdit = () => {
      isEditableTitle.value = false;
    };

    const updateTitle = async (id, value) => {
      emit('update', value);
      isEditableTitle.value = false;
    };

    const updateCustomTitle = async (id, value) => {
      emit('update', value);
      isEditableTitle.value = false;
    };

    // Per-row selected/editable state, mirroring isSelected/isEditableTitle above but scoped to
    // whichever second-order row (a document can have several).
    const selectedSecondOrderIndex = ref(-1);
    const editableSecondOrderIndex = ref(-1);
    const confirmResetSecondOrderIndex = ref(-1);
    const confirmRemoveSecondOrderIndex = ref(-1);

    const onSelectSecondOrder = (soIndex, entry) => {
      if (props.isEditable) {
        selectedSecondOrderIndex.value = soIndex;
      } else {
        emit('select-second-order', entry);
      }
    };

    const cancelSecondOrderEdit = () => {
      editableSecondOrderIndex.value = -1;
    };

    const updateSecondOrderText = (entry, value) => {
      emit('update-second-order', { entry, text: value });
      editableSecondOrderIndex.value = -1;
    };

    const resetSecondOrderReference = () => {
      const entry = props.reference.secondOrderReferences?.[confirmResetSecondOrderIndex.value];
      confirmResetSecondOrderIndex.value = -1;
      if (entry) {
        emit('reset-second-order', entry);
      }
    };

    const removeSecondOrderReference = () => {
      const entry = props.reference.secondOrderReferences?.[confirmRemoveSecondOrderIndex.value];
      confirmRemoveSecondOrderIndex.value = -1;
      if (entry) {
        emit('remove-second-order', entry);
      }
    };

    const handleDragStart = (event) => {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', '');
      event.target.classList.add('dragging-item');
    };

    const handleDragEnd = (event) => {
      event.target.classList.remove('dragging-item');
      event.target.style.cursor = 'pointer';
    };

    const removeReference = (event) => {
      emit('remove', event);
      confirmDeletePopupOpened.value = false;
    };

    const resetReference = () => {
      emit('reset');
      confirmResetPopupOpened.value = false;
    };

    const getThumbUrl = (reference) => {
      if (!reference?.pages || !reference?.pageNumber || !reference.pageNumber[0]) {
        return '';
      }
      return reference.pages[reference.pageNumber[0]]?.small || '';
    };

    return {
      handleDragStart,
      handleDragEnd,
      getThumbUrl,
      isSecondOrderReferencesOrg,
      docType,
      source,
      secondOrderSource,
      title,
      isEditableTitle,
      updateTitle,
      cancelEdit,
      confirmDeletePopupOpened,
      removeReference,
      confirmResetPopupOpened,
      resetReference,
      updateCustomTitle,
      customTitle,
      selectedSecondOrderIndex,
      editableSecondOrderIndex,
      confirmResetSecondOrderIndex,
      confirmRemoveSecondOrderIndex,
      onSelectSecondOrder,
      cancelSecondOrderEdit,
      updateSecondOrderText,
      resetSecondOrderReference,
      removeSecondOrderReference,
      t,
    };
  },
};
</script>

<style lang="scss">
.references-wrapper {
  display: flex;
  flex-direction: column;
  // Each .list-item paints its own white background; staying transparent here lets the grey
  // .references-list background show through the gap between rows.
  gap: $bb-spacing-2;

  &.drop-indicator-before {
    box-shadow: 0 -2px 0 0 $bb-primary-3;
  }

  &.drop-indicator-after {
    box-shadow: 0 2px 0 0 $bb-primary-3;
  }

  .thumbnail-container {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: $bb-spacing-1;
    background-color: $bb-neutral-5;
    width: 48px;
    height: 48px;
    min-width: 48px;
    border: 1px solid $bb-neutral-4;
    overflow: hidden;

    .thumbnail-image {
      max-width: 100%;
      max-height: 100%;
    }

    .default-thumb {
      width: 100%;
      height: 100%;
      background-color: $bb-neutral-4;
    }
  }

  .list-item {
    @include bb-medium-text-styles('xs');
    display: flex;
    align-items: center;
    gap: $bb-spacing-3;
    padding: $bb-spacing-3;
    background-color: $bb-neutral-0;
    border-radius: $bb-spacing-2;
    color: $bb-neutral-1;
    position: relative;
    cursor: pointer;
    user-select: none;
    box-sizing: border-box;
    border: 1px solid transparent;

    &.is-selected {
      border-color: $bb-primary-3;
    }

    .drag-handle {
      flex-shrink: 0;
      color: $bb-neutral-3;
      cursor: grab;
      opacity: 0;
      transition: opacity 0.15s ease-in-out;

      &:active {
        cursor: grabbing;
      }
    }

    &:hover .drag-handle {
      opacity: 1;
    }

    // Wraps the labels row above the thumbnail+title row so labels span the full card width
    // without being vertically centered alongside the thumbnail.
    .reference-content {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: $bb-spacing-2;
    }

    .reference-content-row {
      display: flex;
      align-items: center;
      gap: $bb-spacing-3;
    }

    .footnote-details {
      flex: 1;
      min-width: 0;

      .title-with-custom {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: $bb-spacing-2;

        &__ai {
          color: $bb-neutral-3;
        }

        &__custom {
          width: 100%;
        }
      }
    }
  }

  .second-order-list-item {
    // Space-reserving only — unlike the real drag-handle above, this must never actually appear
    // (these rows aren't draggable), so pin it hidden regardless of the row's :hover state.
    .drag-handle--spacer {
      visibility: hidden !important;
      cursor: default !important;
    }

    .footnote-details {
      width: 300px;
    }

    .second-order-parent-name {
      display: block;
      font-size: 11px;
      font-weight: 500;
      // Same grey as ReferenceDetailsLabels.vue's own "Veeva"/doc-type text (.reference-doc's
      // $bb-neutral-2), instead of the slightly darker $bb-neutral-3.
      color: $bb-neutral-2;
      margin-bottom: $bb-spacing-1;

      &__value {
        font-weight: 500;
      }
    }

    .second-order-more-documents {
      display: block;
      font-size: 11px;
      font-style: italic;
      color: $bb-neutral-3;
      margin-bottom: $bb-spacing-1;
    }
  }
}

.draggable-wrapper {
  &:active {
    cursor: -webkit-grabbing;
    cursor: -moz-grabbing;
    cursor: grabbing;
  }

  &.dragging-item {
    border: 2px solid $bb-primary-1;
    background-color: $bb-neutral-0 !important;

    .delete-button {
      display: none;
    }
  }
}
</style>
